/**
 * OpenAI function schemas + prompting instructions for native tools.
 *
 * Ported from the pre-DO-migration Supabase seed
 * (`supabase/database/data/load_tool_native.sql` at commit d199d7693^).
 * The static-catalog migration in #1426 dropped these fields; restoring
 * them here keeps `native-tools.ts` lean (pure metadata) while still
 * feeding the full schema into tool creation and LLM completions.
 *
 * Keyed by `tool_name` (the function name exposed to the LLM).
 */

export interface NativeToolSchema {
  /** Full OpenAI function schema: { name, description, strict?, parameters: { type, properties, required, additionalProperties } } */
  tool_config: Record<string, unknown>;
  /** Optional prompt injected into the agent's system message when this tool is attached */
  tool_instructions: string | null;
}

export const NATIVE_TOOL_SCHEMAS: Record<string, NativeToolSchema> = {
  "close_conversation": {
    "tool_config": {
      "name": "close_conversation",
      "description": "Closes the current conversation and marks it as resolved",
      // Non-strict, mirroring the `update_kanban_status` sibling below: under strict
      // mode there are no optional fields, so `outcome` outside `required` makes the
      // provider reject the definition and `outcome` inside `required` forces the
      // model to emit it on every call. Requiredness is enforced SERVER-side instead
      // (ops `outcomeRequired`) — and it must stay out of `required` here, or
      // validateAndSanitizeToolParams would refuse the "retry without an outcome"
      // recovery path a re-close depends on.
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "outcome": {
            "type": "string",
            "enum": "[KANBAN_OUTCOME_VALUES]",
            "description": "Required only when the hub's terminal (conversation-ending) status declares outcomes: the closing outcome. Available outcomes: [KANBAN_OUTCOME_NAMES]"
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when the user's issue has been fully resolved."
  },
  "transfer_to_team": {
    "tool_config": {
      "name": "transfer_to_team",
      "description": "Hand off conversation to a human support team",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "team_name": {
            "type": "string",
            "description": "Name of the team to transfer to"
          }
        },
        "required": [
          "team_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when the user needs assistance that requires human intervention. The team will receive the full conversation history."
  },
  "delegate_to_hub": {
    "tool_config": {
      "name": "delegate_to_hub",
      "description": "Delegate a request to another hub. The target hub handles it as a task conversation and returns the result asynchronously.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "request": {
            "type": "string",
            "description": "The request to delegate to the target hub, phrased as a self-contained task."
          }
        },
        "required": [
          "request"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to hand a self-contained request to a configured partner hub. The target hub is set by an admin on the tool; you do not choose it. The result comes back asynchronously — do not wait for it in this turn."
  },
  "start_consult_thread": {
    "tool_config": {
      "name": "start_consult_thread",
      "description": "Ask the consultant configured on this tool a question, in a consult thread the support team can see. The answer may come back in this turn or later.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "question": {
            "type": "string",
            "description": "What you need from the consultant, phrased so it can be answered without further back-and-forth."
          },
          "title": {
            "type": "string",
            "description": "A short label for the consult thread (a few words), so the support team can tell parallel consults apart."
          }
        },
        "required": [
          "question",
          "title"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when answering needs knowledge you do not have and a consultant is configured for it. The consultant is set by an admin; you do not choose it. If the result says the answer will follow, END YOUR TURN — tell the customer you are checking and will come back to them. You will be brought back automatically when the answer arrives; do not poll or call this tool again for the same question."
  },
  "update_kanban_status": {
    "tool_config": {
      "name": "update_kanban_status",
      "description": "Updates the kanban status of the current conversation to track its progress through workflow stages",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "new_kanban_status": {
            "type": "string",
            "enum": "[KANBAN_STATUS_VALUES]",
            "description": "The new kanban status for the conversation. Available statuses: [KANBAN_STATUS_NAMES]"
          },
          "outcome": {
            "type": "string",
            "enum": "[KANBAN_OUTCOME_VALUES]",
            "description": "Required only when moving to the terminal (conversation-ending) status: the closing outcome. Available outcomes: [KANBAN_OUTCOME_NAMES]"
          },
          "scheduled_event_date": {
            "type": "string",
            "description": "Scheduled event date in RFC3339 format with timezone (e.g., \"2024-01-01T00:00:00-03:00\")"
          },
          "event_description": {
            "type": "string",
            "description": "Description of the scheduled event"
          },
          "event_sid": {
            "type": "string",
            "description": "External event ID for integration purposes"
          }
        },
        "required": [
          "new_kanban_status"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to update the kanban status of a conversation to reflect its current stage in the workflow."
  },
  "schedule_followup": {
    "tool_config": {
      "name": "schedule_followup",
      "description": "Schedules a custom manual followup for the current conversation at an exact future time. The system automatically determines the receiver based on conversation status and hub AI mode.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "scheduled_time": {
            "type": "string",
            "description": "Exact time to execute the followup in ISO 8601 format with timezone (e.g., \"2024-01-01T14:30:00-03:00\"). Must be in the future."
          },
          "message": {
            "type": "string",
            "description": "The followup message to send to the customer. Can include AI instructions in brackets like: \"Message text [AI Context: instructions for AI]\""
          }
        },
        "required": [
          "scheduled_time",
          "message"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to schedule a custom followup message at an exact future time. The caller is responsible for choosing appropriate timing (business hours, etc.). This is separate from automatic kanban-based followups."
  },
  "transfer_to_agent": {
    "tool_config": {
      "name": "transfer_to_agent",
      "description": "Hand off conversation to another AI agent",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "agent_name": {
            "type": "string",
            "description": "Name of the AI agent to transfer to"
          }
        },
        "required": [
          "agent_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when you need to hand the conversation to another agent on the same track — a specialist for a specific domain, or back to the entry pilot/copilot acting as a router for a request outside your domain."
  },
  "consult_agent": {
    "tool_config": {
      "name": "consult_agent",
      "description": "Consult an advisor agent for specialized knowledge without transferring the conversation",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "agent_name": {
            "type": "string",
            "description": "Name of the advisor agent to consult for advice"
          },
          "consult_question": {
            "type": "string",
            "description": "The specific question to ask the advisor agent"
          }
        },
        "required": [
          "agent_name",
          "consult_question"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when you need expert advice from an advisor agent but want to maintain conversation ownership. The consulted advisor will provide advice that you can use to help the user."
  },
  "read_file": {
    "tool_config": {
      "name": "read_file",
      "description": "Retrieve a file by path (preferred) or by ID. The file is injected into the conversation context for AI processing.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "Path to the file. Knowledge and skill files live under \"resources/\", exactly as listed in your resource structure or in a file listing — e.g. \"resources/product-docs/references/menu.md\", where the segment after \"resources/\" names the resource. Files attached to this conversation live under \"conversation/\" and are announced in the transcript by that path — e.g. \"conversation/report.pdf\". A shorter form also works for those two and is matched across everything you can read, preferring an exact path over one that merely ends with what you wrote (\"references/menu.md\", or just \"menu.md\"); dropping a middle segment does not resolve, and a bare name carried by both a resource and this conversation is reported as ambiguous so you can retry with the full path. Files from an EARLIER conversation with the same user live under \"conversations/<conversation_id>/\" — e.g. \"conversations/1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed/receipt.pdf\", exactly as the transcript of that conversation announces them (get_conversation). That form takes no shortening: write the mount, the conversation id and the file name in full. Prefer this over file_id: paths are stable, while IDs are regenerated whenever a hub is published."
          },
          "file_id": {
            "type": "string",
            "description": "The file ID to retrieve, from a conversation message or a file listing. Still supported, but prefer path for resource files."
          },
          "resource_id": {
            "type": "string",
            "description": "Optional. Restricts a path to one resource — use it when the same file name exists in more than one resource and the tool reports the reference as ambiguous. Must be a resource linked to this agent."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read a file for AI analysis. Provide either path (preferred — the exact paths appear in your resource structure and in list_files output) or file_id. The file will be injected into the conversation context."
  },
  "download_file": {
    "tool_config": {
      "name": "download_file",
      "description": "Mount a resource file into the code execution sandbox so you can edit it. Path is provider-specific: Anthropic mounts at /tmp/<filename>, OpenAI mounts at /mnt/data/<filename> — use the `sandbox_path` returned in the result. After mounting, use the provider's code-execution tool (Anthropic code_execution view/str_replace/create, or OpenAI code_interpreter Python) to read or modify the file. When finished, call upload_file to commit changes back to the resource library. Bytes never enter conversation context.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "file_id": {
            "type": "string",
            "description": "The resource file_id to mount (from a file listing or read_file — a listing row carries file_id alongside its path). Must be a text file; binary files are rejected."
          }
        },
        "required": [
          "file_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to begin editing a resource file. Returns { sandbox_path, filename, sandbox_file_id }. After this, use your provider's code-execution tool to view and modify the file inside the sandbox, then call upload_file with the sandbox_file_id of the modified file to persist changes."
  },
  "upload_file": {
    "tool_config": {
      "name": "upload_file",
      "description": "Persist a sandbox file to the resource library. Two modes: (1) UPDATE — pass file_id of an existing resource file to overwrite it (after download_file + code-execution edits). (2) CREATE — pass resource_id + filename instead of file_id to add a new file to that resource (after creating the file in the sandbox with code-execution). Requires write_enabled on the agent_resource binding for the target resource. sandbox_file_id is always required.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "file_id": {
            "type": "string",
            "description": "UPDATE mode only: the existing resource file_id to overwrite. Omit when creating a new file."
          },
          "resource_id": {
            "type": "string",
            "description": "CREATE mode only: the resource the new file belongs to. Required when file_id is omitted; ignored when file_id is provided."
          },
          "filename": {
            "type": "string",
            "description": "CREATE mode only: the filename for the new file (e.g. 'pricing_v2.md'). Required when file_id is omitted. MIME type is inferred from the extension; only text files are supported."
          },
          "folder_id": {
            "type": "string",
            "description": "CREATE mode only: optional folder to place the file in. Omit for resource root."
          },
          "sandbox_file_id": {
            "type": "string",
            "description": "The provider-specific file_id of the file inside the sandbox. Anthropic: take from bash_code_execution_tool_result content[].file_id. OpenAI: take from the container_file_citation annotations on code_interpreter_call output. Either one identifies the file as it currently exists in the sandbox."
          }
        },
        "required": [
          "sandbox_file_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to commit either an edit (file_id + sandbox_file_id) or a new file (resource_id + filename + sandbox_file_id). Returns { saved, file_id, file_hash, bytes } on success — the file_id you can reference later. Errors clearly when write access is denied or when neither identifier set is provided."
  },
  "send_files": {
    "tool_config": {
      "name": "send_files",
      "description": "Send files to the end user through their conversation channel (App, WhatsApp, email, etc.)",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "file_ids": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Files to send to the user, each named by path (preferred) or by ID. Paths are the addresses announced in the transcript and in your resource structure — \"conversation/report.pdf\" for a file attached to this conversation, \"conversations/<conversation_id>/receipt.pdf\" for one attached to an earlier conversation with the same user, \"resources/product-docs/price-list.pdf\" for a knowledge or skill file. IDs remain accepted, but they are regenerated whenever a hub is published, so a path is the stable address."
          },
          "folder_ids": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Array of resource folder IDs to send all files from. Alternative to file_ids."
          },
          "message_text": {
            "type": "string",
            "description": "Message text to accompany the files. For WhatsApp, this becomes the caption on the last file. For email, this becomes the email body."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to send files to users. WhatsApp sends files individually with optional caption on last file. Email sends all files as attachments in a single email."
  },
  "list_files": {
    "tool_config": {
      "name": "list_files",
      "description": "Browse the files you can reach, by path. Call it with no path to see the mounts, then pass a mount or a folder path to list what is inside it. Every row carries the exact path read_file and send_files accept.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "path": {
            "type": "string",
            "description": "What to list. Omit it (or pass \"\") for the mounts you can browse — the available mounts are listed here at runtime. \"conversation\" lists the files attached to this conversation. \"resources\" lists every resource mount. \"resources/<resource-slug>\" lists the top level of a knowledge resource or skill, and \"resources/<resource-slug>/<folder>/<subfolder>\" lists that folder. Folder rows come back with their own path, so you drill down by passing back the path of the row you want."
          },
          "query": {
            "type": "string",
            "description": "Search text. Matched against a file's title and its file name. Under a resource path this searches the WHOLE subtree below it, not just the level named, and every result is returned as a full path."
          },
          "tags": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Return files carrying ANY of these tags. Applies to resource files only — conversation attachments carry no tags. Searches the whole subtree, like query."
          },
          "metadata_filter": {
            "type": "object",
            "description": "Filter resource files by their metadata using MongoDB-style operators. Supported: $eq (equal), $ne (not equal), $gt/$gte/$lt/$lte (comparisons), $in/$nin (in/not in array), $contains (substring for text, membership for a list), $and/$or/$not (logical). Nested paths like \"location.city\" are supported, and a bare value means equality. Examples: {\"status\": \"active\"}, {\"priority\": {\"$gte\": 5}}, {\"location.city\": \"Miami\"}, {\"$and\": [{\"status\": \"active\"}, {\"priority\": {\"$gt\": 3}}]}, {\"$not\": {\"status\": \"archived\"}}. A comparison between different types simply does not match. Schema from resource.file_metadata_schema. Searches the whole subtree, like query."
          },
          "limit": {
            "type": "integer",
            "description": "Maximum number of rows to return (default: 50, max: 100). The response reports the total the page was drawn from."
          },
          "offset": {
            "type": "integer",
            "description": "Number of rows to skip for pagination (default: 0)"
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to find out which files exist before reading one. Call it with no path first: that returns the mounts — \"conversation\" for what the user attached here, and one \"resources/<slug>\" per knowledge resource or skill linked to you. Then pass a mount or folder path back to list its contents. Every row carries a `path`; pass that path to read_file to open the file, or to send_files to deliver it. Pass query, tags or metadata_filter to search a whole resource at once instead of walking it folder by folder. Files from EARLIER conversations are not listed here — the transcript of each past conversation announces its own files by path (get_conversation)."
  },
  "list_resource_folders": {
    "tool_config": {
      "name": "list_resource_folders",
      "description": "(deprecated — use list_files) List all folders in resources that this agent has access to. Returns all folders with parent_folder_id for hierarchy reconstruction.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "resource_id": {
            "type": "string",
            "description": "Resource ID to query folders from. Must be one of the knowledge resources linked to this agent (the available ids are listed here at runtime). Required for access control and performance."
          },
          "search_query": {
            "type": "string",
            "description": "Search folders by name"
          },
          "metadata_filter": {
            "type": "object",
            "description": "Filter by metadata using MongoDB-style operators. Supported: $eq (equal), $ne (not equal), $gt/$gte/$lt/$lte (comparisons), $in/$nin (in/not in array), $contains (contains value), $and/$or/$not (logical). Supports nested paths like \"location.city\". Returns clear errors for type mismatches. Examples: {\"status\": \"active\"} for simple equality, {\"priority\": {\"$gte\": 5}} for comparison, {\"location.city\": \"Miami\"} for nested path, {\"$and\": [{\"status\": \"active\"}, {\"priority\": {\"$gt\": 3}}]} for complex logic, {\"$not\": {\"status\": \"archived\"}} for negation. Schema from resource.folder_metadata_schema."
          }
        },
        "required": [
          "resource_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Deprecated: prefer list_files, which walks the same folders by path and needs no resource_id. Still works — lists all folders in a resource, filtered by search_query or metadata, returning folder info with parent_folder_id, file counts, and subfolder counts."
  },
  "list_resource_files": {
    "tool_config": {
      "name": "list_resource_files",
      "description": "(deprecated — use list_files) List files in resources that this agent has access to. Each file is returned with its path — the address read_file accepts — plus metadata.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "resource_id": {
            "type": "string",
            "description": "Resource ID to query files from. Must be one of the knowledge resources linked to this agent (the available ids are listed here at runtime). Required for access control and performance."
          },
          "folder_id": {
            "type": "string",
            "description": "Filter by folder ID. Omit to return ALL files. Use 00000000-0000-0000-0000-000000000000 for root-level files only."
          },
          "search_query": {
            "type": "string",
            "description": "Search files by title or file name"
          },
          "tags": {
            "type": "array",
            "items": {
              "type": "string"
            },
            "description": "Filter by tags (matches files with any of the specified tags)"
          },
          "metadata_filter": {
            "type": "object",
            "description": "Filter by metadata using MongoDB-style operators. Supported: $eq (equal), $ne (not equal), $gt/$gte/$lt/$lte (comparisons), $in/$nin (in/not in array), $contains (contains value), $and/$or/$not (logical). Supports nested paths like \"location.city\". Returns clear errors for type mismatches. Examples: {\"status\": \"active\"} for simple equality, {\"priority\": {\"$gte\": 5}} for comparison, {\"location.city\": \"Miami\"} for nested path, {\"$and\": [{\"status\": \"active\"}, {\"priority\": {\"$gt\": 3}}]} for complex logic, {\"$not\": {\"status\": \"archived\"}} for negation. Schema from resource.file_metadata_schema."
          },
          "limit": {
            "type": "integer",
            "description": "Maximum number of files to return (default: 50, max: 100)"
          },
          "offset": {
            "type": "integer",
            "description": "Number of files to skip for pagination (default: 0)"
          }
        },
        "required": [
          "resource_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Deprecated: prefer list_files, which lists the same files by path, needs no resource_id, and also sees this conversation's attachments. Still works — each row carries a `path`; pass that path to read_file to retrieve the file's content for AI analysis. `file_id` is also returned and still accepted."
  },
  "get_tool_schema": {
    "tool_config": {
      "name": "get_tool_schema",
      "description": "MANDATORY FIRST STEP: Retrieves the complete parameter schema for any tool by name. You MUST call this before using ANY tool to get its exact required parameters. This ensures execute_tool will succeed.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "tool_name": {
            "type": "string",
            "description": "The name of the tool to retrieve the schema for"
          }
        },
        "required": [
          "tool_name"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Mandatory first step before execute_tool. Returns `function_schema` for the target tool — pass `function_schema.parameters` (same shape, same keys) as the `parameters` envelope in your execute_tool call."
  },
  "execute_tool": {
    "tool_config": {
      "name": "execute_tool",
      "description": "Executes any tool by name. You MUST call get_tool_schema first for the target tool to learn its parameter shape, then pass that shape verbatim under `parameters`.",
      "strict": false,
      "parameters": {
        "type": "object",
        "required": [
          "tool_name",
          "parameters"
        ],
        "properties": {
          "tool_name": {
            "type": "string",
            "description": "The name of the tool to execute."
          },
          "parameters": {
            "type": "object",
            "description": "The arguments object for the target tool. Must match `function_schema.parameters` returned by get_tool_schema — same keys, same shape, same required fields. Inner contents are validated by the target tool, not by execute_tool."
          }
        },
        "additionalProperties": false
      }
    },
    "tool_instructions": "MANDATORY WORKFLOW: 1) Call get_tool_schema(tool_name) to receive the target's `function_schema`. 2) Build a `parameters` object that matches `function_schema.parameters` — same property keys, same types, all required fields. 3) Call execute_tool({ tool_name, parameters }). The `parameters` envelope here is identical in shape to `function_schema.parameters`."
  },
  "expand_summary": {
    "tool_config": {
      "name": "expand_summary",
      "description": "Retrieve the original messages of a section in the conversation summary. The summary block at the top of the conversation lists sections with stable `id`s — pass one to get the verbatim messages it covers.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "section_id": {
            "type": "string",
            "description": "The `id` of the section from the <conversation_summary> block at the top of the conversation."
          }
        },
        "required": [
          "section_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use when a summary section is too compressed for the question at hand — e.g. you need the user's exact wording, the precise text of a previous decision, or a tool result that the summary only references. If you pass an unknown `section_id`, the response will include `available_section_ids` so you can retry with a valid one."
  },
  "read_skill": {
    "tool_config": {
      "name": "read_skill",
      "description": "Read a skill's SKILL.md content. Pass a skill_id from the skills linked to this agent (available ids are listed in the skill_id parameter).",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "skill_id": {
            "type": "string",
            "description": "The skill's resource_id. Must be one of the skills linked to this agent (the available ids are listed here at runtime)."
          }
        },
        "required": [
          "skill_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read a skill's instructions. Returns the full SKILL.md content as a string. Use read_skill_file to access other files referenced in the skill."
  },
  "read_skill_file": {
    "tool_config": {
      "name": "read_skill_file",
      "description": "Read a file from a skill by relative path. No need to call read_skill first if you already know the file path.",
      "strict": true,
      "parameters": {
        "type": "object",
        "properties": {
          "skill_id": {
            "type": "string",
            "description": "The skill's resource_id. Must be one of the skills linked to this agent (the available ids are listed here at runtime)."
          },
          "file_path": {
            "type": "string",
            "description": "Relative path from skill root (e.g., \"references/menu.md\")"
          }
        },
        "required": [
          "skill_id",
          "file_path"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read files referenced in SKILL.md. Provide the relative path as shown in the skill content (e.g., \"references/menu.md\"). Returns content for text files or signed URL for binary files."
  },
  "update_state": {
    "tool_config": {
      "name": "update_state",
      "description": "Updates a configured state by merging updates with its current value. Scope (conversation vs user) is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to update. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          },
          "updates": {
            "type": "object",
            "description": "Object with key-value pairs to merge into the current state"
          }
        },
        "required": [
          "state_slug",
          "updates"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to track and update contextual information during conversations. Examples: updating cart items, recording form answers, saving user preferences. The updates are merged with existing state."
  },
  "get_state": {
    "tool_config": {
      "name": "get_state",
      "description": "Retrieves the current value of a configured state. Scope is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to retrieve. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          }
        },
        "required": [
          "state_slug"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to retrieve the current state value. This is useful when you need to check the current state before making decisions or when the state is not already available in the prompt context."
  },
  "reset_state": {
    "tool_config": {
      "name": "reset_state",
      "description": "Resets a configured state to its initial value as defined in the state schema. Scope is inferred from the state's definition.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state to reset. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          }
        },
        "required": [
          "state_slug"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use to reset state to its default initial value. This is useful when you need to clear accumulated state and start fresh, such as clearing a shopping cart or resetting form progress."
  },
  "get_conversations_summary": {
    "tool_config": {
      "name": "get_conversations_summary",
      "description": "Retrieves a timeline of past conversations with the current user, showing summaries and conversation IDs.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "max_conversations": {
            "type": "integer",
            "description": "Maximum number of past conversations to return (default: 10)"
          },
          "max_characters": {
            "type": "integer",
            "description": "Maximum characters in the output (default: 20000, max: 40000). Truncates oldest entries first."
          }
        },
        "required": [],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool at the start of a conversation to retrieve the history of past conversations with this user. Returns summaries with conversation IDs that can be used with get_conversation for full transcripts."
  },
  "get_conversation": {
    "tool_config": {
      "name": "get_conversation",
      "description": "Retrieves the full transcript of a specific past conversation, plus the IDs of the conversations closed immediately before and after it. Use get_conversations_summary first to find conversation IDs.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "conversation_id": {
            "type": "string",
            "description": "The ID of the conversation to retrieve (from get_conversations_summary results)"
          },
          "max_characters": {
            "type": "integer",
            "description": "Maximum characters in the output (default: 30000, max: 60000). Truncates oldest messages first."
          }
        },
        "required": [
          "conversation_id"
        ],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool to read the full transcript of a specific past conversation. First use get_conversations_summary to find conversation IDs, then use this tool to drill into the details. The response may include previous_conversation_id and next_conversation_id — the conversations closed nearest in time either side of this one, which you can pass straight back to this tool to keep moving through the user's history. They are neighbours by closing time, not a fixed chain, and either may be absent."
  },

  // ---------------------------------------------------------------------------
  // Tools added to the TS catalog after the Supabase migration — schemas
  // reconstructed from the handler signatures in
  // apps/backend/src/tools/native/wayai/*.ts.
  // ---------------------------------------------------------------------------

  "set_state_path": {
    "tool_config": {
      "name": "set_state_path",
      "description": "Set a specific path within a state object (dot/array path) without overwriting the rest of the state. Scope is inferred from the state's definition.",
      // strict:false matches the other state ops so legacy composed-tool YAML
      // mapping `state_scope` (now ignored at runtime) doesn't trip OpenAI
      // strict-mode validators on in-flight calls.
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "state_slug": {
            "type": "string",
            "description": "Stable slug of the state variable to update. Pass the slug (the left half), not the display name. Available states (slug + display name): [STATE_SLUG_VALUES]"
          },
          "path": {
            "type": "array",
            "items": { "type": "string" },
            "description": "Path within the state object, as an ordered list of keys."
          },
          "value": {
            "description": "The value to write at the given path. Any JSON value is allowed."
          }
        },
        "required": ["state_slug", "path", "value"],
        "additionalProperties": false
      }
    },
    "tool_instructions": "Use this tool when you need to update a single field inside a structured state object without replacing the entire state. Provide the full path from the root of the state object."
  },
  // NEVER OFFERED TO A MODEL, unlike every other entry in this file — and it is
  // `model_callable: false` on the catalog entry that delivers that, NOT the role
  // restriction beside it. An earlier version of this comment credited
  // `assignable_roles: ['monitor']`, which was wrong: a monitor runs a DISPATCHING
  // turn on `idle` (only the synchronous side-call sets `omit_tools`), and
  // `PATCH /api/setup/agents/:id` can re-role an agent that keeps the rows it
  // holds — so the role gate left the tool offerable on two paths.
  //
  // The schema exists because the catalog requires one per tool (the parity check
  // at the bottom of `native-tools.ts`) and because it is the one place the two
  // arguments are described for whoever writes the rule.
  //
  // `tool_instructions: null` FOLLOWS FROM THAT: instructions are injected into an
  // agent's system message when the tool is attached, and this tool is attached to
  // an agent whose model is never told it exists.
  // NEVER OFFERED TO A MODEL, like `insert_note` below and for the same reason:
  // `model_callable: false` on the catalog entry. A monitor's RULE selects it and
  // the monitor runtime carries it out — it never reaches `handleTool`, because
  // running a callee means re-entering the side-call with the caller's cascade
  // state, which no `ToolContext` carries.
  "run_monitor": {
    "tool_config": {
      "name": "run_monitor",
      "description": "Run another monitor on this hub. The named monitor judges the same conversation and acts on its own rules; nothing it finds is returned here.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "monitor_name": {
            "type": "string",
            "description": "The name of the monitor to run. It must be a monitor on this hub whose trigger is manual."
          }
        },
        "required": ["monitor_name"],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  },
  "insert_note": {
    "tool_config": {
      "name": "insert_note",
      "description": "Insert an admin-authored note into the answering agent's context for this turn only. The note is written by the administrator, not by a model; the monitor's variables are substituted into it.",
      "strict": false,
      "parameters": {
        "type": "object",
        "properties": {
          "template": {
            "type": "string",
            "description": "The note text. Write {{variable_name}} to substitute one of this monitor's own variables. Unresolved names render as nothing and are recorded on the audit row."
          },
          "keep_in_history": {
            "type": "boolean",
            "description": "Default false: the note briefs this turn only and is never stored. Set true to also record it as an internal message the support team can see and the customer never receives."
          }
        },
        "required": ["template"],
        "additionalProperties": false
      }
    },
    "tool_instructions": null
  }
};
